import { DoughnutChart, Tooltip } from '@dataplace.ai/ui-components/atoms'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import useCheckIfPrinting from 'apps/placeme/src/customHooks/useCheckIfPrinting'
import { ChartWrapper, ColorDot, Content, HeaderTitle, Legend, LegendItem, LegendLabel, Value } from './TrafficByDaypart.styles'
import { backgroundColors, daypartTimeRanges, daypartTranslationMap } from './constants'
import { StyledWrapper, TitleWrapper } from '../SharedOverview.styles'
import CatchmentInfoComponent from '../components/CatchmentInfoComponent'
import { NoDataGuard } from '../components/NoDataGuard'
import { NO_OVERVIEW_DATA } from '../constants'

const TooltipWrapper = styled.div`
  display: flex;
  flex-direction: column;
  color: white;
  max-width: 180px;
`

export interface TrafficByDaypartProps {
  catchment: {
    mode: string
    length: number
  }
  dayparts: {
    daypart: string
    sharePct: number
  }[]
}

export type TrafficByDaypartData = TrafficByDaypartProps | typeof NO_OVERVIEW_DATA
const TILE_TITLE_KEY = 'placeme.analyse.overview.week_traffic.tile_name'

const TRAFFIC_BY_DAYPART = ({
  dayparts, catchment,
}: TrafficByDaypartProps): JSX.Element => {
  const { t } = useTranslation()
  const { isPrinting } = useCheckIfPrinting()

  const sortedDayparts = [...dayparts].sort((a, b) => {
    const order = Object.keys(daypartTranslationMap)
    return order.indexOf(a.daypart) - order.indexOf(b.daypart)
  })

  const labels = sortedDayparts.map(({ daypart }) =>
    t(daypartTranslationMap[daypart as keyof typeof daypartTranslationMap]))
  const data = sortedDayparts.map(({ sharePct }) => sharePct)

  return (
    <StyledWrapper $width='fit-content'>
      <TitleWrapper>
        {t(TILE_TITLE_KEY)}
        <CatchmentInfoComponent {...catchment} />
      </TitleWrapper>
      <HeaderTitle>{t('placeme.analyse.overview.dayparts_traffic.dayparts')}</HeaderTitle>
      <Content>
        <ChartWrapper>
          <DoughnutChart
            colors={backgroundColors}
            cutoutPercentage={65}
            data={data}
            height={isPrinting ? 100 : 200}
            innerLabels
            labelFontSize={isPrinting ? 8 : 12}
            labels={labels}
            layoutPadding={isPrinting ? 4 : 8}
            width={isPrinting ? 100 : 200}
          />
        </ChartWrapper>

        <Legend>
          {sortedDayparts.map((item, i) => (
            <LegendItem key={item.daypart}>
              <Tooltip
                content={(
                  <TooltipWrapper>
                    {daypartTimeRanges[item.daypart as keyof typeof daypartTimeRanges]}
                  </TooltipWrapper>
                )}
                position='bottom center'
              >
                <LegendLabel>
                  <ColorDot color={backgroundColors[i]} />
                  {t(daypartTranslationMap[item.daypart as keyof typeof daypartTranslationMap])}
                </LegendLabel>
              </Tooltip>
              <Value>
                {item.sharePct}
                %
              </Value>
            </LegendItem>
          ))}
        </Legend>
      </Content>
    </StyledWrapper>
  )
}

export function TrafficByDaypart({ data }: {data: TrafficByDaypartData}): JSX.Element {
  return (
    <NoDataGuard
      component={TRAFFIC_BY_DAYPART}
      data={data}
      title={TILE_TITLE_KEY}
    />
  )
}
